import { Link } from '@tanstack/react-router';

import { ApiError } from '../../api/client';
import type { IntelligenceReport, IntelligenceUsage } from '../../api/intelligenceReportSchemas';
import { Drawer } from '../../admin/components/Drawer';
import { ErrorBanner } from '../../components/ErrorBanner';
import { LoadingState } from '../../components/LoadingState';
import { insightLink, reportDateLabel, usageLine } from '../../lib/intelligence-view';
import { AdvisorOrb } from './AdvisorOrb';
import { useIntelligenceReport, useIntelligenceReports, useRequestIntelligenceReport } from './queries';

const DRAWER_TITLE = 'Dale Agents';
const DRAWER_INSIGHT_LIMIT = 3;
const STAMP_ORB = 40;
const REQUEST_LABEL = 'Pedir un informe nuevo';
const REQUESTING_LABEL = 'Tu equipo está revisando el negocio…';
const EMPTY_LABEL = 'Tu equipo todavía no ha escrito informes.';

type IntelligenceDrawerProps = { open: boolean; onClose: () => void };

/** The latest report in compact form, without leaving the current screen. */
export function IntelligenceDrawer({ open, onClose }: IntelligenceDrawerProps) {
  return (
    <Drawer open={open} title={DRAWER_TITLE} onClose={onClose}>
      <DrawerBody onClose={onClose} />
    </Drawer>
  );
}

// Mounted only while the drawer is open, so nothing is read until then.
function DrawerBody({ onClose }: { onClose: () => void }) {
  const reportsQuery = useIntelligenceReports();
  const latest = reportsQuery.data?.data[0];
  const reportQuery = useIntelligenceReport(latest?.id);
  const request = useRequestIntelligenceReport();
  const usage = reportsQuery.data?.usage;
  const report = reportQuery.data;
  const isLoading = reportsQuery.isLoading || (Boolean(latest) && reportQuery.isLoading);
  const error = reportsQuery.error ?? reportQuery.error ?? request.error;

  return (
    <div className="intel-drawer">
      {isLoading ? <LoadingState label="Cargando el informe…" /> : null}
      {error ? <ErrorBanner message={errorMessage(error)} /> : null}
      {reportsQuery.data && !latest ? <p className="row-sub intel-drawer-empty">{EMPTY_LABEL}</p> : null}
      {report ? <LatestReport report={report} onClose={onClose} /> : null}
      <div className="intel-drawer-actions">
        {report ? (
          <Link to="/inteligencia/$reportId" params={{ reportId: report.id }} className="btn small intel-drawer-open" onClick={onClose}>
            Ver informe completo
          </Link>
        ) : null}
        <button type="button" className="btn small secondary intel-drawer-request" disabled={request.isPending || !reportsQuery.data} onClick={() => request.mutate()}>
          {request.isPending ? REQUESTING_LABEL : REQUEST_LABEL}
        </button>
      </div>
      {usage ? <UsageLine usage={usage} /> : null}
    </div>
  );
}

function LatestReport({ report, onClose }: { report: IntelligenceReport; onClose: () => void }) {
  const date = reportDateLabel(report.createdAt, new Date());
  const director = report.personas.find((persona) => persona.personaId === 'direccion');
  const insights = report.insights.slice(0, DRAWER_INSIGHT_LIMIT);
  // The stamp already names Júpiter when it signs; otherwise its line says who spoke.
  const voiceName = director && report.signer.personaId !== 'direccion' ? director.name : undefined;
  // The lead finding usually is the headline; say it once.
  const showHeadline = insights[0]?.title !== report.headline;

  return (
    <article className="intel-latest-card">
      <div className="intel-stamp">
        <AdvisorOrb personaId={report.signer.personaId} size={STAMP_ORB} />
        <div className="intel-stamp-text">
          <p className="intel-stamp-who">{`${report.signer.name} · ${report.signer.title}`}</p>
          {date ? <p className="intel-stamp-meta">{date}</p> : null}
        </div>
      </div>
      {showHeadline ? <h3 className="intel-drawer-headline">{report.headline}</h3> : null}
      {director?.narration ? (
        <blockquote className="intel-drawer-narration">
          <p>{director.narration}</p>
          {voiceName ? <footer className="intel-drawer-voice">{voiceName}</footer> : null}
        </blockquote>
      ) : null}
      {report.notice ? <p role="note" className="notice intel-notice intel-drawer-notice">{report.notice}</p> : null}
      {insights.length > 0 ? (
        <ul className="group rows intel-drawer-insights">
          {insights.map((insight, index) => {
            const link = insightLink(insight);
            return (
              <li className="row intel-drawer-insight" key={`${insight.type}-${index}`}>
                <div className="grow">
                  <p className="row-title intel-drawer-insight-title">{insight.title}</p>
                  <p className="row-sub intel-drawer-insight-detail">{insight.detail}</p>
                  <Link to={link.to} search={link.search} className="linklike intel-drawer-insight-action" onClick={onClose}>
                    {link.label}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </article>
  );
}

function UsageLine({ usage }: { usage: IntelligenceUsage }) {
  return <p className="row-sub intel-drawer-usage">{usageLine(usage)}</p>;
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : 'No pudimos cargar tu equipo. Intenta de nuevo.';
}
